// ---------------------------------------------
// file: /src/Pages/NewReservation/index.js
// ---------------------------------------------

import React, { useState, useEffect } from 'react';
import { fields } from './Config/formConfig.js';
import './css/newReservation.css';
import './css/mobile.css';
import './css/animations.css';
import './css/style.css';

import useFonts from '../../Hooks/useFonts.js';
import transformFonts from '../../Utils/transformFonts.js';
import computeAlignmentValues from '../../Utils/computeAlignmentValues.js';

import ReservationModal from './Structure/PopUp.js';
import ReservationFullScreen from './Structure/FullScreen.js';
import ReservationForm from './Structure/Form.js';
import ReservationStyles from './Style/ReservationStyles.js';
import {
  defaultAppearanceData,
  defaultFonts,
  defaultSettings,
  defaultTheme,
} from './Style/defaults.js';
import { darkenHexColor, isColorTooBright } from './Style/colorUtils.js';

/**
 * Now receives `restaurantData` **and** `initialMenuTitle` from PhotoGallery.
 */
export default function NewReservation({
  restaurantData,
  selectedMenuId,
  initialMenuTitle = '',
  onReservationSuccess,
  onCloseRequest
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Inject custom fonts
  useFonts(restaurantData);

  // -------------------------------------------------------------------------
  //  Global “modal-open” body class
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isModalOpen) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');

    return () => document.body.classList.remove('modal-open');
  }, [isModalOpen]);

  // -------------------------------------------------------------------------
  //  Destructure with fallbacks
  // -------------------------------------------------------------------------
  const {
    timeblocks = [],
    menu = [],
    mededeling = '',
  } = restaurantData;

  const generalSettings = restaurantData['general-settings'] || {};
  window.generalSettings = generalSettings; // for backward‐compat
  const theme = defaultTheme;
  const colors = defaultAppearanceData;
  const settings = defaultSettings;
  
  // -------------------------------------------------------------------------
  //  Alignment helpers
  // -------------------------------------------------------------------------
  const alignment = settings?.alignment || 'fullScreenColor';
  const { alignmentBottomValue, alignmentTopValue, alignmentMarginValue } =
    computeAlignmentValues(alignment);

  // -------------------------------------------------------------------------
  //  Font handling
  // -------------------------------------------------------------------------
  let { fonts } = restaurantData;
  fonts = transformFonts(fonts || defaultFonts);
  const { titleFont, subtitleFont, buttonFont, labelFont } = fonts;

  // -------------------------------------------------------------------------
  //  Form background
  // -------------------------------------------------------------------------
  // Title is no longer customizable via settings; default to "Reserveer Nu"
  const title = 'Reserveer Nu';
  const backgroundType = colors?.backgroundType || 'solid';

  let formSectionBackgroundStyle = '';
  if (backgroundType === 'solid') {
    formSectionBackgroundStyle = `
      .form-section {
        background-color: ${colors?.backgroundColor || '#FFFFFF'} !important;
      }
    `;
  } else if (backgroundType === 'gradient') {
    const start = colors?.gradientStartColor || '#FFFFFF';
    const end = colors?.gradientEndColor || '#000000';
    formSectionBackgroundStyle = `
      .form-section {
        background-image: linear-gradient(${start}, ${end}) !important;
      }
    `;
  } else if (backgroundType === 'animated') {
    formSectionBackgroundStyle = `
      .form-section {
        background-color: ${colors?.backgroundColor || '#FFFFFF'} !important;
      }
      .form-section.animated-background {
        background-size: 800% 800%;
      }
    `;
  }

  // -------------------------------------------------------------------------
  //  Utility: hex → rgba
  // -------------------------------------------------------------------------
  const hexToRgba = (hex, opacity) => {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };
  const backgroundColorTransparant = hexToRgba(
    colors?.backgroundColor || '#FFFFFF',
    0.03,
  );

  // Bubble-view color tweaks
  let bubbleViewColor = colors?.backgroundColor || '#000';
  if (isColorTooBright(bubbleViewColor))
    bubbleViewColor = darkenHexColor(bubbleViewColor, 0.2);

  // -------------------------------------------------------------------------
  //  Form element to be reused in modal / full-screen
  // -------------------------------------------------------------------------
  const formContent = (
    <ReservationForm
      title={title}
      settings={settings}
      generalSettings={generalSettings}
      fields={fields}
      timeblocks={timeblocks}
      menu={menu}
      mededeling={mededeling}
      restaurantData={restaurantData}
      selectedMenuId={selectedMenuId}
      initialMenuTitle={initialMenuTitle}     /* ← new prop */
      onReservationSuccess={onReservationSuccess}
    />
  );

  // -------------------------------------------------------------------------
  //  JSX
  // -------------------------------------------------------------------------
  const backgroundBlur = settings?.backgroundBlur || 'sharpPicture';

  return (
    <div id="mateza">
      <div
        className={`new-reservation-page ${
          window.viewMode === 'popup' ? 'popup-mode' : ''
        } ${backgroundBlur === 'blurBackground' ? 'blur-background' : ''}`}
        style={{
          '--widget-background-color': colors?.widgetBackgroundColor,
          '--widget-text-color': colors?.widgetTextColor,
          '--text-color': colors?.textColor,
          '--background-color': colors?.backgroundColor,
          '--background-color-transparant': backgroundColorTransparant,
          '--container-color': colors?.containerColor,
          '--button-color': colors?.buttonColor,
          '--button-text-color': colors?.buttonTextColor,
          '--alignment-bottom': alignmentBottomValue,
          '--alignment-top': alignmentTopValue,
          '--alignment-margin': alignmentMarginValue,
          '--bubble-view-color': bubbleViewColor,
        }}
      >
        {window.viewMode === 'full-screen' ? (
          <ReservationFullScreen theme={theme} formContent={formContent} />
        ) : (
          <ReservationModal
            isModalOpen={isModalOpen}
            isClosing={isClosing}
            openModal={() => setIsModalOpen(true)}
            closeModal={() => {
              setIsClosing(true);
              setTimeout(() => {
                setIsModalOpen(false);
                setIsClosing(false);
                onCloseRequest(); // propagate close
              }, 300);
            }}
            formContent={formContent}
          />
        )}

        <ReservationStyles
          fonts={{ labelFont, titleFont, subtitleFont, buttonFont }}
          formSectionBackgroundStyle={formSectionBackgroundStyle}
        />

        {window.viewMode === 'full-screen' ? (
          <div
            className={`form-section ${
              backgroundType === 'animated' ? 'animated-background' : ''
            }`}
            style={
              alignment === 'fullScreenPicture'
                ? { minHeight: '0', bottom: 0 }
                : { position: 'absolute', right: 0, minHeight: '120vh', bottom: 0 }
            }
          >
            {formContent}
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
